import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format, formatDistanceToNow } from "date-fns";
import { startCase } from "lodash";
import { ClockIcon, Package2, RefreshCcw } from "lucide-react";
import {
  apiProductStockOrderItemsStockOrderIdFormatGetCollectionOptions,
  apiProductStockOrdersIdFormatGetOptions,
} from "@/generated/api/@tanstack/react-query.gen.ts";
import { cn } from "@/lib/utils.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import ContainerInset from "@/components/container-inset.tsx";
import CustomTag from "@/components/custom-tag.tsx";
import { PageErrorNoData } from "@/components/page-error.tsx";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout.tsx";
import StockOrderFormSkeleton from "@/app/(authenticated)/$merchantId/stock-orders/_component/stock-order-form-skeleton.tsx";
import StockOrderForm from "@/app/(authenticated)/$merchantId/stock-orders/_component/stock-order-form.tsx";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider.tsx";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/stock-orders/$id/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { selectedMerchant } = useMerchantProvider();

  const [dataFetching, setDataFetching] = useState(false);

  const orderByIdQuery = useQuery({
    ...apiProductStockOrdersIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": selectedMerchant?.merchAccountId ?? "unknown",
      },
      path: { id: id },
    }),
    enabled: !!selectedMerchant?.merchAccountId,
  });

  const productDetailByLocationQuery = useQuery({
    ...apiProductStockOrderItemsStockOrderIdFormatGetCollectionOptions({
      headers: {
        "X-Merchant-Account-Id": selectedMerchant?.merchAccountId ?? "unknown",
      },
      path: { stock_order_id: id },
    }),
    enabled: !!selectedMerchant?.merchAccountId,
  });

  if (orderByIdQuery.isPending || productDetailByLocationQuery.isPending) {
    return (
      <ContainerInset
        breadcrumbs={[
          {
            title: "Stock Orders",
            href: `/${selectedMerchant?.merchAccountId}/stock-orders`,
          },
          {
            title: "Detail",
            href: "",
          },
        ]}
      >
        <PageLayoutRoot>
          <PageLayoutHeader
            title="Stock Order Details"
            description="View and manage stock order details, quantities, and status"
          />
          <PageLayoutContent className="space-y-6">
            {/* Status Banner Skeleton */}
            <Skeleton className="h-20 w-full rounded-lg" />

            <StockOrderFormSkeleton />
          </PageLayoutContent>
        </PageLayoutRoot>
      </ContainerInset>
    );
  }

  if (orderByIdQuery.error || productDetailByLocationQuery.error) {
    return <PageErrorNoData />;
  }

  if (!orderByIdQuery.data || !productDetailByLocationQuery.data) {
    return <PageErrorNoData />;
  }

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Stock Orders",
          href: `/${selectedMerchant?.merchAccountId}/stock-orders`,
        },
        {
          title: "Detail",
          href: "",
        },
      ]}
    >
      <PageLayoutRoot>
        {/* Page Header */}
        <PageLayoutHeader
          title="Stock Order Details"
          description="View and manage stock order details, quantities, and status"
        />

        <PageLayoutContent className="space-y-6">
          {/* Status Banner */}
          <div
            className={cn(
              // Base styles
              "flex items-center justify-between",
              "rounded-lg border",
              "px-5 py-4",
              "bg-linear-to-r",

              // Status-specific styles
              {
                "border-blue-200/60 from-blue-50/40 to-blue-50/90":
                  orderByIdQuery.data?.status === "ordered",
                "border-emerald-200/60 from-emerald-50/40 to-emerald-50/90":
                  orderByIdQuery.data?.status === "received",
                "border-red-200/60 from-red-50/40 to-red-50/90":
                  orderByIdQuery.data?.status === "cancelled",
              }
            )}
          >
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground/70 text-sm font-medium">
                  Order Status
                </span>
                <div className="flex items-center gap-2.5">
                  <CustomTag
                    size="large"
                    label={startCase(orderByIdQuery.data?.status)}
                    className={cn("font-medium tracking-wide", {
                      "bg-blue-100/80 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400":
                        orderByIdQuery.data?.status === "ordered",
                      "bg-emerald-100/80 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400":
                        orderByIdQuery.data?.status === "received",
                      "bg-red-100/80 text-red-700 dark:bg-red-950/60 dark:text-red-400":
                        orderByIdQuery.data?.status === "cancelled",
                    })}
                  />
                  <span
                    className={cn("text-sm", {
                      "text-blue-600/80":
                        orderByIdQuery.data?.status === "ordered",
                      "text-emerald-600/80":
                        orderByIdQuery.data?.status === "received",
                      "text-red-600/80":
                        orderByIdQuery.data?.status === "cancelled",
                    })}
                  >
                    {getStatusDescription(orderByIdQuery.data?.status)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {orderByIdQuery.data?.createdAt && (
                <div className="flex flex-col gap-1 text-right text-sm">
                  <span className="text-muted-foreground/70">Last Updated</span>
                  <div className="space-y-0.5">
                    <div className="font-medium tabular-nums">
                      {orderByIdQuery.data?.updatedAt
                        ? format(
                            new Date(orderByIdQuery.data?.updatedAt),
                            "MMM d, yyyy"
                          )
                        : ""}
                    </div>
                    <div className="text-muted-foreground/60 flex items-center justify-end gap-1.5 text-xs">
                      <ClockIcon className="size-3" />
                      <span className="tabular-nums">
                        {orderByIdQuery.data?.updatedAt
                          ? format(
                              new Date(orderByIdQuery.data?.updatedAt),
                              "h:mm a"
                            )
                          : ""}
                      </span>
                      <span className="text-muted-foreground/40">•</span>
                      <span>
                        {orderByIdQuery.data?.updatedAt
                          ? formatDistanceToNow(
                              new Date(orderByIdQuery.data?.updatedAt),
                              {
                                addSuffix: true,
                                includeSeconds: true,
                              }
                            )
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <StockOrderForm
              id={orderByIdQuery.data?.stockOrderId}
              status={orderByIdQuery.data?.status}
              onUpdateSuccess={async () => {
                setDataFetching(true);
                await Promise.all([
                  orderByIdQuery.refetch(),
                  productDetailByLocationQuery.refetch(),
                ]);
                setDataFetching(false);
              }}
              defaultValues={{
                invoice: orderByIdQuery.data?.invoice,
                location: orderByIdQuery.data?.location,
                comment: orderByIdQuery.data?.comment,
                stockOrderItems: productDetailByLocationQuery.data?.member?.map(
                  (item) => ({
                    productDetailByLocation:
                      item.productDetailByLocation?.["@id"] ?? "unknown",
                    quantity: item.quantity
                      ? parseFloat(item.quantity).toString()
                      : "",
                  })
                ),
              }}
            />
            {dataFetching && (
              <div className="bg-background/20 absolute inset-0 z-10 flex items-center justify-center rounded-lg backdrop-blur-[1px]">
                <div className="flex flex-col items-center gap-3">
                  <RefreshCcw className="text-primary size-6 animate-spin" />
                  <span className="text-muted-foreground text-sm font-medium">
                    Updating...
                  </span>
                </div>
              </div>
            )}
          </div>
        </PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}

const getStatusDescription = (status: string | undefined) => {
  switch (status) {
    case "ordered":
      return "Order is being processed";
    case "received":
      return "Stock has been received";
    case "cancelled":
      return "Order has been cancelled";
    default:
      return "";
  }
};
